<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from 'dashboard/routes/dashboard/projects/ProjectEmptyState.vue';

// Advisory requirements (requisitos) checklist for a tramite. The host injects an `api` adapter
// (get/reevaluate/waive — each taking ownerId first) and the `ownerId`. Requirements are
// catalog-driven (configured on the TramiteType) and auto-evaluated; an agent can re-evaluate the
// whole list or waive an individual item. No hard gate — status is informational.
const props = defineProps({
  api: { type: Object, required: true },
  ownerId: { type: [String, Number], required: true },
});

const { t } = useI18n();

const STATUS_PILL = {
  pending: 'bg-n-alpha-2 text-n-slate-11',
  met: 'bg-n-teal-3 text-n-teal-11',
  unmet: 'bg-n-ruby-3 text-n-ruby-11',
  affirmed: 'bg-n-teal-3 text-n-teal-11',
  waived: 'bg-n-amber-3 text-n-amber-11',
};

const requirements = ref([]);
const isLoading = ref(false);
const isReevaluating = ref(false);
const waivingId = ref(null);
const waiveNote = ref('');

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await props.api.get(props.ownerId);
    requirements.value = data || [];
  } catch (error) {
    requirements.value = [];
  } finally {
    isLoading.value = false;
  }
};

const reevaluate = async () => {
  if (isReevaluating.value) return;
  isReevaluating.value = true;
  try {
    const { data } = await props.api.reevaluate(props.ownerId);
    requirements.value = data || [];
  } catch (error) {
    useAlert(t('TRAMITES.REQUIREMENTS.REEVALUATE_ERROR'));
  } finally {
    isReevaluating.value = false;
  }
};

const startWaive = id => {
  waivingId.value = id;
  waiveNote.value = '';
};
const cancelWaive = () => {
  waivingId.value = null;
};
const confirmWaive = async id => {
  try {
    await props.api.waive(props.ownerId, id, waiveNote.value);
    waivingId.value = null;
    await load();
  } catch (error) {
    useAlert(t('TRAMITES.REQUIREMENTS.WAIVE_ERROR'));
  }
};

const reasonOf = requirement => requirement.result?.reason;

watch(() => props.ownerId, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex justify-end">
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="isReevaluating"
        :label="t('TRAMITES.REQUIREMENTS.REEVALUATE')"
        @click="reevaluate"
      />
    </div>

    <ProjectEmptyState
      v-if="!requirements.length && !isLoading"
      compact
      icon="i-lucide-list-checks"
      :title="t('TRAMITES.REQUIREMENTS.EMPTY')"
    />

    <div v-else class="flex flex-col divide-y divide-n-weak/60">
      <div
        v-for="requirement in requirements"
        :key="requirement.id"
        data-test="requirement-row"
        class="flex flex-col gap-1.5 py-2.5"
      >
        <div class="flex gap-2 items-center">
          <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
            {{ requirement.label }}
          </span>
          <span
            class="shrink-0 rounded-full px-1.5 py-0.5 text-xs font-medium"
            :class="STATUS_PILL[requirement.status] || STATUS_PILL.pending"
          >
            {{ t(`TRAMITES.REQUIREMENTS.STATUS.${requirement.status}`) }}
          </span>
          <Button
            v-if="!requirement.satisfied"
            v-tooltip.top="t('TRAMITES.REQUIREMENTS.WAIVE')"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-badge-check"
            @click="startWaive(requirement.id)"
          />
        </div>

        <p v-if="requirement.description" class="pl-1 text-xs text-n-slate-10">
          {{ requirement.description }}
        </p>
        <p v-if="reasonOf(requirement)" class="pl-1 text-xs text-n-slate-9">
          {{ reasonOf(requirement) }}
        </p>
        <p v-if="requirement.status === 'waived' && requirement.review_note" class="pl-1 text-xs text-n-amber-11">
          {{ requirement.review_note }}
        </p>

        <div v-if="waivingId === requirement.id" class="flex flex-col gap-2 pl-1">
          <textarea
            v-model="waiveNote"
            rows="2"
            :placeholder="t('TRAMITES.REQUIREMENTS.WAIVE_NOTE_PLACEHOLDER')"
            class="px-2.5 py-1.5 w-full text-sm rounded-md border resize-none reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
          <div class="flex gap-2 justify-end items-center">
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              :label="t('TRAMITES.REQUIREMENTS.CANCEL')"
              @click="cancelWaive"
            />
            <Button
              size="xs"
              color="amber"
              :label="t('TRAMITES.REQUIREMENTS.WAIVE_CONFIRM')"
              @click="confirmWaive(requirement.id)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
